import { ReactNode, useContext, useEffect, useMemo, useState } from "react";
import LoadingState from "../components/configuration/loading-states";
import { CorporationOperation } from "../serviceoperation/CorporationOperation";
import { CorpDetailAPIEntity } from "../serviceoperation/entity/CorporationOperationEntity";
import { useMSALInterceptor } from "../Util/useMSALInterceptor";
import { AuthenticationContext } from "./authentication-context";
import { CompanyContext, ICompanyContext } from "./company-context";
import { message } from "antd";

interface CompanyContextProviderProps {
  children: ReactNode;
  setLoading: (loading: LoadingState) => void;
}

const CompanyContextProvider = (props: CompanyContextProviderProps) => {
  // Token Interceptor..
  // This will handle the tokens. :D

  // Remove this, and call the token directly in the service layer.
  useMSALInterceptor();

  const { children, setLoading } = props;
  const [companyData, setCompanyData] = useState<Array<CorpDetailAPIEntity>>(
    JSON.parse(localStorage.getItem("CompanyData") ?? "[]"),
  );
  const [selectedCompany, setSelectedCompany] = useState<CorpDetailAPIEntity>(
    JSON.parse(localStorage.getItem("selectedCompany") ?? "{}"),
  );
  const [isCompanyDataLoading, setIsCompanyDataLoading] = useState<boolean>(
    companyData.length === 0,
  );

  // This will fetch the Company and Selected Company data
  // from Local Storage.
  useEffect(() => {
    const selectedComp = JSON.parse(
      localStorage.getItem("selectedCompany") || "{}",
    );
    const compData = JSON.parse(localStorage.getItem("CompanyData") || "[]");
    setSelectedCompany(selectedComp);
    setCompanyData(compData);
  }, []);

  const userCompanies = useContext(AuthenticationContext)?.userInfo
    ?.employeeCorporation;

  //const accessToken = useContext(AuthenticationContext)?.accessToken;

  const updateSelectedCompany = (
    companyId: number,
    companyOverride?: CorpDetailAPIEntity,
  ) => {
    const fallbackCompany = companyData.find(
      (comp) => comp.corporationId === companyId,
    );
    const nextCompany = companyOverride ?? fallbackCompany;

    if (!nextCompany) return;

    setSelectedCompany(nextCompany);
    localStorage.setItem("selectedCompany", JSON.stringify(nextCompany));

    if (companyOverride) {
      setCompanyData((prev) => {
        const exists = prev.some(
          (comp) => comp.corporationId === companyOverride.corporationId,
        );
        const updated = exists
          ? prev.map((comp) =>
              comp.corporationId === companyOverride.corporationId
                ? companyOverride
                : comp,
            )
          : [...prev, companyOverride];

        localStorage.setItem("CompanyData", JSON.stringify(updated));
        return updated;
      });
    }
  };

  const values: ICompanyContext = useMemo(
    () => ({
      companyData,
      selectedCompany,
      isCompanyDataLoading,
      updateSelectedCompany,
    }),
    [companyData, selectedCompany, isCompanyDataLoading],
  );

  useEffect(() => {
    const saveDataToLocalStorage = (key: string, data: unknown) => {
      if (data) {
        localStorage.setItem(key, JSON.stringify(data));
      }
    };

    saveDataToLocalStorage("selectedCompany", selectedCompany);
    saveDataToLocalStorage("CompanyData", companyData);
  }, [selectedCompany, companyData]);

  useEffect(() => {
    // Keep loading shimmer active until user membership data is known.
    if (!userCompanies) {
      setIsCompanyDataLoading(true);
      return;
    }

    if (userCompanies.length === 0 || companyData.length > 0) {
      setIsCompanyDataLoading(false);
      return;
    }

    if (userCompanies && userCompanies.length > 0 && companyData.length === 0) {
      setIsCompanyDataLoading(true);
      const compOps = new CorporationOperation();
      compOps
        .getCorpAsync()
        .then((data) => {
          if (data.statusCode === 200) {
            if (data.data) {
              setCompanyData(data.data);
              setSelectedCompany(data.data[0]);
            }
          } else {
            message.error("An error has occured trying to fetch corporation.");
          }
        })
        .catch(() => {
          setLoading(LoadingState.Error);
        })
        .finally(() => {
          setIsCompanyDataLoading(false);
        });
      return;
    }
  }, [userCompanies, companyData]);
  return (
    <CompanyContext.Provider value={values}>{children}</CompanyContext.Provider>
  );
};

export default CompanyContextProvider;
